/**
 * Écran « Export des données » (RGPD, droit à la portabilité) — version Cloud, administrateur seulement.
 * Deux exports : toute l'entreprise, ou une personne. Formats JSON ou CSV (Excel).
 */
import { esc } from '@presentation/shell/format';
import { LIMITE_EXPORTS_RGPD, messageErreurExportRgpd } from '@application/rgpd/ExportRgpd';
import { STYLE_AIDE, type Notifier } from '@presentation/views/comptabilite/panelKit';

export type FormatExport = 'json' | 'csv';

export interface PersonneExportable {
  id: string;
  libelle: string;
}

export interface ExportRgpdDeps {
  personnes: readonly PersonneExportable[];
  exporterTout(format: FormatExport): Promise<{ blob: Blob; filename: string }>;
  exporterPersonne(id: string, format: FormatExport): Promise<{ blob: Blob; filename: string }>;
  telecharger(blob: Blob, filename: string): void;
  notify: Notifier;
}

const P = 'rgpd-export';

export function mountExportRgpdPanel(host: HTMLElement, deps: ExportRgpdDeps): void {
  const options = deps.personnes
    .map((p) => `<option value="${esc(p.id)}">${esc(p.libelle)}</option>`)
    .join('');
  host.innerHTML = `
    <div class="card" id="${P}-panel" aria-labelledby="${P}-titre">
      <h2 class="card-title" id="${P}-titre">Export des données (droit à la portabilité)</h2>
      <p style="${STYLE_AIDE}">Cet export remet à une personne, ou à vous-même, une copie des données de l’entreprise dans un fichier lisible :
        fiches, bulletins, registres, liste des documents, comptes utilisateurs et journal. Les mots de passe et le contenu des documents n’y figurent jamais.
        Chaque export est enregistré dans le journal. Limite : ${esc(LIMITE_EXPORTS_RGPD)}. Réservé à l’administrateur.</p>
      <fieldset style="border:1px solid #DAE0E4;border-radius:6px;padding:10px 14px;margin:0 0 12px;">
        <legend style="font-weight:700;color:var(--marine);padding:0 6px;">Format du fichier</legend>
        <label style="display:flex;gap:8px;align-items:center;font-size:13px;margin:4px 0;">
          <input type="radio" name="${P}-format" value="json" checked> JSON (fichier informatique complet, recommandé)</label>
        <label style="display:flex;gap:8px;align-items:center;font-size:13px;margin:4px 0;">
          <input type="radio" name="${P}-format" value="csv"> CSV (s’ouvre dans Excel, séparateur « ; »)</label>
      </fieldset>
      <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:16px;">
        <button type="button" class="btn btn-primary" id="${P}-tout">Exporter toute l’entreprise</button>
      </div>
      <h3 style="font-size:14px;color:var(--marine);margin:0 0 8px;">Exporter les données d’une personne</h3>
      <div class="field" style="max-width:420px;">
        <label for="${P}-personne">Personne concernée</label>
        <select id="${P}-personne" ${deps.personnes.length === 0 ? 'disabled' : ''}>${options || '<option value="">Aucune fiche disponible</option>'}</select>
      </div>
      <div style="margin-top:10px;">
        <button type="button" class="btn btn-secondary" id="${P}-une" ${deps.personnes.length === 0 ? 'disabled' : ''}>Exporter cette personne</button>
      </div>
      <p id="${P}-erreur" role="alert" class="form-error-summary" style="margin-top:12px;" hidden></p>
      <p id="${P}-statut" role="status" style="${STYLE_AIDE}margin-top:12px;"></p>
    </div>`;

  const erreur = host.querySelector<HTMLElement>(`#${P}-erreur`)!;
  const statut = host.querySelector<HTMLElement>(`#${P}-statut`)!;
  const format = (): FormatExport =>
    host.querySelector<HTMLInputElement>(`input[name="${P}-format"]:checked`)?.value === 'csv' ? 'csv' : 'json';

  const lancer = async (
    bouton: HTMLButtonElement,
    executer: () => Promise<{ blob: Blob; filename: string }>,
    description: string,
  ): Promise<void> => {
    erreur.hidden = true;
    erreur.textContent = '';
    statut.textContent = 'Préparation du fichier…';
    bouton.disabled = true;
    try {
      const fichier = await executer();
      deps.telecharger(fichier.blob, fichier.filename);
      statut.textContent = `${description} : fichier « ${fichier.filename} » téléchargé.`;
      deps.notify.success('Export téléchargé.');
    } catch (err) {
      const code = (err as { statusCode?: number } | null)?.statusCode;
      const message = messageErreurExportRgpd(code);
      statut.textContent = '';
      erreur.textContent = message;
      erreur.hidden = false;
      deps.notify.error(message);
    } finally {
      bouton.disabled = false;
    }
  };

  const toutBtn = host.querySelector<HTMLButtonElement>(`#${P}-tout`)!;
  toutBtn.addEventListener('click', () => {
    const f = format();
    void lancer(toutBtn, () => deps.exporterTout(f), 'Export de l’entreprise');
  });
  const uneBtn = host.querySelector<HTMLButtonElement>(`#${P}-une`)!;
  uneBtn.addEventListener('click', () => {
    const id = host.querySelector<HTMLSelectElement>(`#${P}-personne`)?.value ?? '';
    if (!id) return;
    const f = format();
    void lancer(uneBtn, () => deps.exporterPersonne(id, f), 'Export de la personne');
  });
}
